import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  X, Warning, CheckCircle, XCircle, ArrowsMerge, User, Phone, Envelope, MapPin,
} from "@phosphor-icons/react";

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 text-xs">
    <Icon size={12} className="mt-0.5 text-[#8A8780] shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">{label}</div>
      <div className="text-[#1A1A1A] break-all">{value || <span className="text-[#B5B2A8]">—</span>}</div>
    </div>
  </div>
);

const Reason = ({ text }) => {
  const isEmail = text === "email_match";
  const sim = text.startsWith("name_similar_in_ward:");
  const score = sim ? parseFloat(text.split(":")[1]) : null;
  const tone = isEmail ? "bg-[#F9E4B7] text-[#7A5A11]"
    : score != null && score >= 0.95 ? "bg-[#F4C7B9] text-[#7A2E18]"
    : "bg-[#F9E4B7] text-[#7A5A11]";
  const label = isEmail
    ? "Email match"
    : sim ? `Name similar in ward · ${(score * 100).toFixed(0)}%`
    : text;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.15em] ${tone}`}>
      {label}
    </span>
  );
};

/**
 * DuplicateReviewModal
 *  - Shows the pending customer alongside every soft-duplicate match.
 *  - Staff can: merge into any match, approve anyway (not a duplicate), or reject.
 */
export default function DuplicateReviewModal({ customer, onClose, onResolved }) {
  const [source, setSource] = useState(customer);
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rejectNote, setRejectNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [fresh, ...others] = await Promise.all([
          api.get(`/customers/${customer.id}`).then(r => r.data),
          ...((customer.duplicate_flag?.soft_matches || []).map(m =>
            api.get(`/customers/${m.id}`).then(r => ({ ...r.data, _meta: m })).catch(() => null)
          )),
        ]);
        setSource(fresh);
        setMatches(others.filter(Boolean));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [customer.id]);

  const mergeInto = async (target) => {
    if (!window.confirm(`Merge "${source.name}" into "${target.name}"? The pending record will be rejected and any missing fields on "${target.name}" will be filled in.`)) return;
    setBusy(true);
    try {
      await api.post(`/customers/${source.id}/merge`, { target_id: target.id });
      toast.success(`Merged into ${target.name}`);
      onResolved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const approve = async () => {
    if (!window.confirm(`Approve "${source.name}" as a NEW customer (not a duplicate)?`)) return;
    setBusy(true);
    try {
      await api.post(`/customers/${source.id}/verify`, { decision: "active", note: "reviewed — not a duplicate" });
      toast.success("Customer approved");
      onResolved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const reject = async () => {
    if (!window.confirm(`Reject "${source.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/customers/${source.id}/verify`, {
        decision: "rejected",
        note: rejectNote || "reviewed — rejected as duplicate",
      });
      toast.success("Customer rejected");
      onResolved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-5xl my-6 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="dup-review-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#F9E4B7] text-[#7A5A11] flex items-center justify-center">
              <Warning size={18} weight="fill" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Duplicate review</div>
              <div className="font-heading text-lg">Potential duplicate flagged</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded" data-testid="dup-close">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#8A8780]">Loading comparison…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Source (pending) */}
            <div className="p-6 border-r border-[#E5E2D9] bg-[#FAF9F4]">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#F9E4B7] text-[#7A5A11] mb-3">
                <Warning size={10} weight="fill" /> New · pending
              </div>
              <div className="font-heading text-xl" data-testid="dup-source-name">{source.name}</div>
              <div className="font-mono text-[11px] text-[#2D5A27] tracking-wider mt-0.5">
                {source.customer_code}
              </div>
              <div className="mt-4 space-y-3">
                <Field icon={Phone} label="Phone" value={source.phone} />
                <Field icon={Envelope} label="Email" value={source.email} />
                <Field icon={User} label="Customer type" value={source.customer_type} />
                <Field icon={MapPin} label="Address" value={source.address} />
                <Field icon={MapPin} label="GPS"
                       value={source.latitude ? `${source.latitude.toFixed(5)}, ${source.longitude.toFixed(5)}` : null} />
              </div>
              <div className="mt-6 pt-4 border-t border-[#E5E2D9] space-y-2">
                <button onClick={approve} disabled={busy}
                        data-testid="dup-approve"
                        className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                  <CheckCircle size={14} weight="fill" /> Not a duplicate — approve
                </button>
                <input type="text" value={rejectNote}
                       onChange={(e) => setRejectNote(e.target.value)}
                       placeholder="Reason for rejection (optional)"
                       data-testid="dup-reject-note"
                       className="w-full h-9 px-3 border border-[#E5E2D9] rounded-md text-xs" />
                <button onClick={reject} disabled={busy}
                        data-testid="dup-reject"
                        className="w-full h-10 border border-[#C25D38] text-[#7A2E18] hover:bg-[#F4C7B9]/30 disabled:opacity-60 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                  <XCircle size={14} weight="fill" /> Reject
                </button>
              </div>
            </div>

            {/* Matches */}
            <div className="p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-3">
                {matches.length} possible match{matches.length === 1 ? "" : "es"}
              </div>
              {matches.length === 0 && (
                <div className="text-sm text-[#8A8780] py-6">No live duplicates remain — they may have been deleted since registration.</div>
              )}
              <div className="space-y-4">
                {matches.map((m) => (
                  <div key={m.id} className="border border-[#E5E2D9] rounded-md p-4"
                       data-testid={`dup-match-${m.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="font-medium">{m.name}</div>
                        <div className="font-mono text-[11px] text-[#2D5A27]">{m.customer_code}</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] ${
                        m.status === "active" ? "bg-[#D4E0D1] text-[#1A3317]"
                        : m.status === "rejected" ? "bg-[#F4C7B9] text-[#7A2E18]"
                        : "bg-[#F9E4B7] text-[#7A5A11]"
                      }`}>{m.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(m._meta?.reasons || []).map((r, i) => <Reason key={i} text={r} />)}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                      <Field icon={Phone} label="Phone" value={m.phone} />
                      <Field icon={Envelope} label="Email" value={m.email} />
                      <Field icon={MapPin} label="Address" value={m.address} />
                      <Field icon={User} label="Type" value={m.customer_type} />
                    </div>
                    <button onClick={() => mergeInto(m)} disabled={busy || m.status === "rejected"}
                            data-testid={`dup-merge-${m.id}`}
                            className="w-full h-9 bg-[#1E3A5F] hover:bg-[#264a78] disabled:opacity-60 text-white rounded-md text-xs font-medium inline-flex items-center justify-center gap-2">
                      <ArrowsMerge size={13} weight="bold" /> Merge this pending customer into "{m.name}"
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
